/**
 * 照片示例组件
 * 在用户上传图片前展示合格/不合格的照片示例
 * 帮助用户了解什么样的照片能获得更好的分析结果
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

type LabelKey = "correct" | "tooMany" | "tooDark" | "tooSmall";

interface PhotoExample {
  src: string;
  isPass: boolean;
  labelKey: LabelKey;
}

const examples: PhotoExample[] = [
  { src: "/images/exam1.jpg", isPass: true, labelKey: "correct" },
  { src: "/images/exam2.jpg", isPass: false, labelKey: "tooMany" },
  { src: "/images/exam3.jpg", isPass: false, labelKey: "tooDark" },
  { src: "/images/exam4.jpg", isPass: false, labelKey: "tooSmall" },
];

// 标签默认值映射
const labelDefaults: Record<LabelKey, string> = {
  correct: "正确",
  tooMany: "太多",
  tooDark: "太暗",
  tooSmall: "太小",
};

interface PhotoExamplesProps {
  className?: string;
  onClose?: () => void;
}

export const PhotoExamples: React.FC<PhotoExamplesProps> = ({
  className,
  onClose,
}) => {
  const { t } = useTranslation("assessments");
  const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>({});

  // 检查所有图片是否已加载
  const allImagesLoaded = examples.every((_, index) => loadedImages[index]);

  // 获取标签文本
  const getLabel = (key: LabelKey): string => {
    const translationKey = `photoExamples.labels.${key}` as
      | "photoExamples.labels.correct"
      | "photoExamples.labels.tooMany"
      | "photoExamples.labels.tooDark"
      | "photoExamples.labels.tooSmall";
    return t(translationKey, labelDefaults[key]);
  };

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => ({ ...prev, [index]: true }));
  };

  // 如果图片未全部加载完成，显示骨架屏
  if (!allImagesLoaded) {
    return (
      <Card
        className={cn(
          "w-full max-w-[340px] sm:w-fit sm:max-w-none mx-auto relative select-none",
          className,
        )}
      >
        <CardContent className="pt-3 sm:pt-4 pb-3 sm:pb-4 px-3 sm:px-5">
          {/* 顶部提示文字骨架 */}
          <Skeleton className="h-4 w-3/4 mx-auto mb-3 sm:mb-4" />

          {/* 图片网格骨架 */}
          <div className="flex justify-center gap-2 sm:gap-3 md:gap-4">
            {examples.map((example, index) => (
              <div
                key={index}
                className="flex flex-col items-center gap-1.5 sm:gap-2 w-16 sm:w-16 md:w-20 lg:w-24 shrink-0"
              >
                {/* 图片容器 */}
                <Skeleton className="w-full aspect-square rounded-lg" />
                {/* 隐藏的图片用于预加载 */}
                <img
                  src={example.src}
                  alt={getLabel(example.labelKey)}
                  loading="eager"
                  decoding="async"
                  onLoad={() => handleImageLoad(index)}
                  className="hidden"
                />
                {/* 标签骨架 */}
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        "w-full max-w-[340px] sm:w-fit sm:max-w-none mx-auto relative select-none",
        className,
      )}
    >
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20 transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      )}
      <CardContent className="pt-3 sm:pt-4 pb-3 sm:pb-4 px-3 sm:px-5">
        {/* 顶部提示文字 */}
        <p className="text-[10px] sm:text-xs text-muted-foreground text-center mb-3 sm:mb-4">
          {t("photoExamples.hint", "清晰、对焦准确的照片能获得更好的分析结果")}
        </p>

        {/* 图片网格 */}
        <div className="flex justify-center gap-2 sm:gap-3 md:gap-4">
          {examples.map((example, index) => (
            <div
              key={index}
              className="flex flex-col items-center gap-1.5 sm:gap-2 w-16 sm:w-16 md:w-20 lg:w-24 shrink-0"
            >
              {/* 图片容器 */}
              <div className="relative w-full aspect-square rounded-lg overflow-hidden">
                <img
                  src={example.src}
                  alt={getLabel(example.labelKey)}
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover pointer-events-none"
                  draggable={false}
                />
              </div>
              {/* 图片标签 */}
              <div
                className={cn(
                  "flex items-center gap-1 text-[10px] sm:text-xs text-center leading-tight",
                  example.isPass
                    ? "text-green-600 dark:text-green-400"
                    : "text-muted-foreground",
                )}
              >
                <div
                  className={cn(
                    "rounded-full p-0.5",
                    example.isPass
                      ? "bg-green-500 dark:bg-green-400"
                      : "bg-red-500 dark:bg-red-400",
                  )}
                >
                  {example.isPass ? (
                    <Check className="w-2 h-2 text-white" strokeWidth={3} />
                  ) : (
                    <X className="w-2 h-2 text-white" strokeWidth={3} />
                  )}
                </div>
                <span>{getLabel(example.labelKey)}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PhotoExamples;
